import type { ZudokuConfig } from "zudoku";
import { ReportCustomPage } from "./pages/ReportCustomPage";
import { SubscriptionsCustomPage } from "./pages/SubscriptionsCustomPage";
import { SubscriptionsCustomTestPage } from "./pages/SubscriptionsCustomTestPage";
// import { TieredContentPage } from "./pages/TieredContentPage";
import { WelcomePage } from "./pages/WelcomePage";
import { SupportContactPage } from "./pages/SupportContactPage";
import { UserManagementPage } from "./pages/UserManagementPage";
import { SubscriptionManagementPage } from "./pages/SubscriptionManagementPage";
import { AddOnPacksPage } from "./pages/AddOnPacksPage";
import { TickerUsagePage } from "./pages/TickerUsagePage";

/**
 * Developer Portal Configuration
 * For more information, see:
 * https://zuplo.com/docs/dev-portal/zudoku/configuration/overview
 */

// NOTE managing access throuh zuplo/zudoku/auth0 integration is a nightmare. Failed attempts stay here
// function getRoles(auth: any, rolesClaim: string): string[] {
//   // ID token claim (already present on auth.user when logged in)
//   const idRoles = Array.isArray(auth?.user?.[rolesClaim]) ? auth.user[rolesClaim] : [];
//   // Access token claim (only read if payload is already present; don’t fetch)
//   const atPayload = auth?.accessTokenPayload;
//   const atNamespaced = Array.isArray(atPayload?.[rolesClaim]) ? atPayload[rolesClaim] : [];
//   // Some setups might also put plain roles (not recommended), so fallback:
//   const atPlain = Array.isArray(atPayload?.roles) ? atPayload.roles : [];
//   return [...new Set([...idRoles])]; //, ...atNamespaced, ...atPlain])];
// }
// const ROLES_CLAIM = (ns: string) => `${ns}/roles`;
// function getRolesSm(auth: any, claim: string): string[] {
//   const id = Array.isArray(auth?.user?.[claim]) ? auth.user[claim] : [];
//   const atp = Array.isArray(auth?.accessTokenPayload?.[claim]) ? auth.accessTokenPayload[claim] : [];
//   const at = Array.isArray(auth?.accessToken?.[claim]) ? auth.accessToken[claim] : [];
//   const md = auth?.user_metadata?.["role"] ? auth.user_metadata["role"] : "";
//   return [...new Set([...id, ...atp, ...at, md])];
// }

// Capture the native fetch BEFORE Zudoku monkey-patches window.fetch to proxy
// all requests through the user's gateway. We need the real fetch to reach
// api.zuploedge.com (the Zuplo management API) directly.
const _nativeFetch = typeof fetch !== "undefined" ? fetch.bind(globalThis) : undefined;

// Inject a legal disclaimer footer into every page.
const _applyFooterDisclaimer = (): void => {
  if (typeof document === "undefined") return;
  if (document.getElementById("sdp-disclaimer-footer")) return; // already injected
  const bar = document.createElement("div");
  bar.id = "sdp-disclaimer-footer";
  bar.style.cssText = [
    "position:fixed","bottom:0","left:0","right:0","z-index:9999",
    "background:#f9fafb","border-top:1px solid #e5e7eb",
    "padding:6px 16px","font-size:11px","line-height:1.4",
    "color:#6b7280","text-align:center",
  ].join(";");
  bar.textContent = 'Security DataPoint LLC (\u201cSDP\u201d) is not a Nationally Recognized Statistical Rating Organization (NRSRO), credit rating agency, registered investment adviser, broker-dealer, or insurance entity. SDP scores are externally observable assessments intended as supplemental informational inputs. They are not credit ratings, security certifications, compliance attestations, or investment recommendations. Absence of signals does not imply absence of risk; presence of signals does not imply confirmed compromise. Refer to SDP\u2019s Subscription Terms of Service at securitydatapoint.com/legal/subscription-terms for complete terms, warranty disclaimers, and liability limitations.';
  document.body.appendChild(bar);
  // Push page content up so the bar doesn't overlap the bottom of the page
  document.body.style.paddingBottom = (bar.offsetHeight + 8) + "px";
};
if (typeof window !== "undefined") {
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", _applyFooterDisclaimer);
  } else {
    _applyFooterDisclaimer();
  }
}

// Reveal the API Keys dropdown entry only for superadmins.
// Theme CSS hides it by default; this script un-hides it if the stored token
// proves the current user has the superadmin permission.
// Reveal Subscription pages only for internal_use tier users.
// CSS hides them by default; this script un-hides them if the stored token
// proves the current user has tier === "internal_use".
const _applySubscriptionVisibility = (): void => {
  if (typeof window === "undefined" || typeof localStorage === "undefined") return;
  try {
    const raw = localStorage.getItem("auth-state");
    const token = JSON.parse(raw ?? "{}")?.state?.providerData?.accessToken as string | undefined;
    if (!token) return; // not logged in — keep hidden
    const payload = JSON.parse(atob(token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/")));
    if (payload["https://api.securitydatapoint.com/tier"] === "internal_use") {
      const style = document.createElement("style");
      style.id = "sdp-subscriptions-internal-visible";
      style.textContent = `a[href="/subscriptions"] { display: revert !important; } a[href="/test_subscriptions"] { display: revert !important; }`;
      document.head.appendChild(style);
    }
  } catch { /* ignore */ }
};
if (typeof window !== "undefined") {
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", _applySubscriptionVisibility);
  } else {
    _applySubscriptionVisibility();
  }
}

const _applyApiKeyVisibility = (): void => {
  if (typeof window === "undefined" || typeof localStorage === "undefined") return;
  try {
    const raw = localStorage.getItem("auth-state");
    const token = JSON.parse(raw ?? "{}")?.state?.providerData?.accessToken as string | undefined;
    if (!token) return; // not logged in — keep hidden
    const payload = JSON.parse(atob(token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/")));
    if (payload["https://api.securitydatapoint.com/permission"] === "superadmin") {
      const style = document.createElement("style");
      style.id = "sdp-api-keys-superadmin-visible";
      style.textContent = `a[href="/settings/api-keys"] { display: revert !important; }`;
      document.head.appendChild(style);
    }
  } catch { /* ignore */ }
};
if (typeof window !== "undefined") {
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", _applyApiKeyVisibility);
  } else {
    _applyApiKeyVisibility();
  }
}

const config: ZudokuConfig = {
  site: {
    title: "SDP Portal",
    logo: {
      // Local file served from docs/public/ — no external CDN needed.
      // Image is 600×415; width≈58px yields ~40px height (aspect ratio preserved).
      src: {
        light: "/sdp_logo.jpg",
        dark: "/sdp_logo.jpg",
      },
      width: "58px",
    },
  },
  theme: {
    // Hide the API Keys dropdown link for non-superadmins by default.
    // _applyApiKeyVisibility (above) re-enables it for superadmins at page load.
    customCss: `
      a[href="/settings/api-keys"] { display: none !important; }
      a[href="/subscriptions"] { display: none !important; }
      a[href="/test_subscriptions"] { display: none !important; }

      /* Widen the API route list sidebar so long route names are fully visible */
      :root {
        --zudoku-sidebar-width: 22rem;
      }
      [class*="sidebar"], aside[class*="api"], nav[class*="api"] {
        min-width: 22rem !important;
        width: 22rem !important;
      }

    `,
  },
  metadata: {
    title: "SDP Portal",
    description: "SDP Portal",
  },
  navigation: [
    {
      type: "custom-page",
      path: "/",
      label: "Welcome",
      element: <WelcomePage />,
    },
    {
      type: "custom-page",
      path: "/report",
      label: "Company Report",
      element: <ReportCustomPage />,
      display: "auth",
    },
    {
      type: "category",
      label: "Documentation",
      display: "auth",
      items: [
        {
          type: "category",
          label: "Getting Started",
          icon: "sparkles",
          items: [
            {
              type: "doc",
              file: "introduction",
            },
            {
              type: "doc",
              file: "markdown",
            },
          ],
        },
        {
          type: "link",
          to: "/api",
          label: "API Endpoints",
        },
      ],
    },
    {
      type: "category",
      label: "Articles",
      icon: "book-open",
      display: "auth",
      items: [
        {
          type: "doc",
          file: "articles/index",
        },
        {
          type: "doc",
          file: "articles/getting-started",
        },
        {
          type: "doc",
          file: "articles/methodology",
        },
        {
          type: "doc",
          file: "articles/rate-limits",
        },
        {
          type: "doc",
          file: "articles/api-error-codes",
        },
        {
          type: "doc",
          file: "articles/available-companies",
        },
      ],
    },
    {
      type: "category",
      label: "Changelog · v1.2.14",
      icon: "list-ordered",
      display: "auth",
      items: [
        {
          type: "doc",
          file: "changelog/index",
        },
        {
          type: "doc",
          file: "changelog/v1.2.14",
        },
      ],
    },
    {
      type: "custom-page",
      path: "/support",
      label: "Contact Support",
      element: <SupportContactPage />,
      display: "auth",
    },
    {
      type: "category",
      label: "Admin",
      display: "auth",
      items: [
        {
          type: "custom-page",
          path: "/user-management",
          label: "User Management",
          element: <UserManagementPage />,
        },
        {
          type: "custom-page",
          path: "/subscription-management",
          label: "Subscription Management",
          element: <SubscriptionManagementPage />,
        },
        {
          type: "custom-page",
          path: "/addon-packs",
          label: "Add-on Packs",
          element: <AddOnPacksPage />,
        },
        {
          type: "custom-page",
          path: "/ticker-usage",
          label: "Subscription Usage",
          element: <TickerUsagePage />,
        },
      ],
    },
    // {
    //   type: "custom-page",
    //   path: "/tiered-content",
    //   label: "Tiered Content",
    //   element: <TieredContentPage />,
    //   display: "auth", // TODO hide this page for now
    // },
    {
      type: "custom-page",
      path: "/subscriptions",
      label: "Subscriptions",
      element: <SubscriptionsCustomPage />,
      display: "auth",
    },
    {
      type: "custom-page",
      path: "/test_subscriptions",
      label: "Test Subscriptions",
      element: <SubscriptionsCustomTestPage />,
      display: "auth",
    },
  ],
  redirects: [],
  apis: [
    {
      type: "file",
      input: "../config/routes.oas.json",
      path: "api",
      options: {
        examplesLanguage: "shell", // Default language for code examples
        disablePlayground: false, // Disable the interactive API playground
        // disableSidecar: false, // Disable the sidecar completely
        showVersionSelect: "if-available", // Control version selector visibility
        expandAllTags: true, // Control initial expanded state of tag categories
      },
    },
  ],
  authentication: {
    // IMPORTANT: This is a demo Auth0 configuration.
    // In a real application, you should replace these values with your own identity provider's configuration!
    // This configuration WILL NOT WORK with custom domains!
    // domain: "auth.zuplo.site",
    // clientId: "f8I87rdsCRo4nU2FHf0fHVwA9P7xi7Ml",
    // audience: "https://api.example.com/",
    // For more information, see: https://zuplo.com/docs/dev-portal/zudoku/configuration/authentication
    type: "auth0",
    // NOTE we should use variables with ZUDOKU_PUBLIC_ prefix in this file + this variables should not be secrets.
    // TODO One or more of your connections are currently using Auth0 development keys and should not be used in production!
    domain: process.env.ZUDOKU_PUBLIC_AUTH0_DOMAIN,
    clientId: process.env.ZUDOKU_PUBLIC_AUTH0_CLIENT_ID,
    audience: process.env.ZUDOKU_PUBLIC_AUTH0_AUDIENCE,
  },
  protectedRoutes: {
    // Check if user has access
    "/api/*": ({ auth, context }) => auth.isAuthenticated,
    "/articles/*": ({ auth, context }) => auth.isAuthenticated,
    "/changelog/*": ({ auth, context }) => auth.isAuthenticated,
    "/report": ({ auth, context }) => auth.isAuthenticated,
    "/subscriptions": ({ auth, reasonCode }) => {
      if (!auth.isAuthenticated) return reasonCode.UNAUTHORIZED;
      if (typeof localStorage === "undefined") return reasonCode.FORBIDDEN;
      try {
        const raw = localStorage.getItem("auth-state");
        const token = JSON.parse(raw ?? "{}")?.state?.providerData?.accessToken;
        if (!token) return reasonCode.FORBIDDEN;
        const payload = JSON.parse(atob(token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/")));
        return payload["https://api.securitydatapoint.com/tier"] === "internal_use"
          ? true
          : reasonCode.FORBIDDEN;
      } catch {
        return reasonCode.FORBIDDEN;
      }
    },
    "/test_subscriptions": ({ auth, reasonCode }) => {
      if (!auth.isAuthenticated) return reasonCode.UNAUTHORIZED;
      if (typeof localStorage === "undefined") return reasonCode.FORBIDDEN;
      try {
        const raw = localStorage.getItem("auth-state");
        const token = JSON.parse(raw ?? "{}")?.state?.providerData?.accessToken;
        if (!token) return reasonCode.FORBIDDEN;
        const payload = JSON.parse(atob(token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/")));
        return payload["https://api.securitydatapoint.com/tier"] === "internal_use"
          ? true
          : reasonCode.FORBIDDEN;
      } catch {
        return reasonCode.FORBIDDEN;
      }
    },
    // "/tiered-content": ({ auth, context }) => auth.isAuthenticated,
    "/markdown": ({ auth, context }) => auth.isAuthenticated,
    "/introduction": ({ auth, context }) => auth.isAuthenticated,
    "/support": ({ auth, context }) => auth.isAuthenticated,
    "/user-management": ({ auth, context }) => auth.isAuthenticated,
    "/subscription-management": ({ auth, context }) => auth.isAuthenticated,
    "/addon-packs": ({ auth, context }) => auth.isAuthenticated,
    "/ticker-usage": ({ auth, context }) => auth.isAuthenticated,
    // /settings/api-keys is restricted to superadmins only.
    // reasonCode.UNAUTHORIZED → login dialog (unauthenticated users)
    // reasonCode.FORBIDDEN    → "Access Denied" page (authenticated non-superadmins)
    "/settings/api-keys": ({ auth, reasonCode }) => {
      if (!auth.isAuthenticated) return reasonCode.UNAUTHORIZED;
      if (typeof localStorage === "undefined") return reasonCode.FORBIDDEN; // SSR bypass render
      try {
        const raw = localStorage.getItem("auth-state");
        const token = JSON.parse(raw ?? "{}")?.state?.providerData?.accessToken;
        if (!token) return reasonCode.FORBIDDEN;
        const payload = JSON.parse(atob(token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/")));
        return payload["https://api.securitydatapoint.com/permission"] === "superadmin"
          ? true
          : reasonCode.FORBIDDEN;
      } catch {
        return reasonCode.FORBIDDEN;
      }
    },
  },

  // NOTE managing access throuh zuplo/zudoku/auth0 integration is a nightmare. Failed attempts stay here
  // protectedRoutes: {
  //   // Check if user has access
  //   "/api_test_0/*": ({ auth, context }) =>
  //     auth.isAuthenticated && ["basic", "pro", "premium", "internal_use"].includes(auth.user?.user_metadata["role"]),
  // },
  // protectedRoutes: {
  //   "/api_test_1/*": ({ auth, context }) => {
  //     const idPerms = (auth?.user && auth.user[process.env.ZUDOKU_PUBLIC_MY_AUTH_DEV_AUDIENCE+"/roles"]) || [];
  //     const atPerms = (auth?.accessTokenPayload && auth.accessTokenPayload.roles) || [];
  //     console.debug('auth.user keys:', auth.user && Object.keys(auth.user));
  //     console.debug('accessTokenPayload:', auth.accessTokenPayload);
  //     return auth.isAuthenticated && (idPerms.includes("basic") || atPerms.includes("basic"));
  //   },

  //   "/api_test_2/*": ({ auth, context }: any) => {
  //     if (!auth?.isAuthenticated) return false;
  //     // Prefer a hard-coded constant to avoid env-substitution issues in guards
  //     const ns = process.env.ZUDOKU_PUBLIC_MY_AUTH_DEV_AUDIENCE;
  //     const rolesClaim = `${ns}/roles`;
  //     const roles = getRoles(auth, rolesClaim);
  //     // Optional debug
  //     // console.debug('[guard]/api/*', {
  //     //   userKeys: auth.user ? Object.keys(auth.user) : [],
  //     //   hasAccessTokenPayload: Boolean(auth.accessTokenPayload),
  //     //   roles,
  //     // });
  //     return roles.includes("basic");
  //   },

  //   "/api_test_3/*": ({ auth, context }) =>
  //     auth.isAuthenticated && auth.user?.role === "basic",
  // },

  // protectedRoutes: {
  //   "/api_test_4/*": ({ auth, context }: any) => {
  //     // const audience = context.env.ZUDOKU_PUBLIC_MY_AUTH_DEV_AUDIENCE;
  //     // console.debug('context audience:', audience);
  //     const audience = process.env.ZUDOKU_PUBLIC_MY_AUTH_DEV_AUDIENCE;
  //     console.debug('processAudience:', audience);
  //     if (!auth?.isAuthenticated) return false;
  //     // const roles = getRolesSm(auth, ROLES_CLAIM(audience));
  //     const roles = getRolesSm(auth, "audience");
  //     console.debug('roles:', roles);
  //     return roles.includes("basic");
  //   },
  // },
  apiKeys: {
    enabled: true,
    // Optional customizations:
    // title: "Manage Your API Keys",
    // description: "Create and manage API keys for accessing the SDP API",
    // createButtonLabel: "Generate New API Key",
    // deleteConfirmation: "Are you sure you want to delete this API key?",
    // maxKeys: 5, // Maximum number of keys a user can create
    // keyPrefixes: ["sdp_live_", "sdp_test_"], // Custom key prefixes

    // NOTE: /settings/api-keys is in protectedRoutes with a superadmin guard.
    // getConsumers also enforces the superadmin check as a second layer of defence.
    // Unauthenticated users are blocked by the api-keys plugin's own ProtectedRoute component.
    getConsumers: async (context) => {
      const _metaEnv = import.meta.env as any;

      // Log the authenticated user's tier and permission for observability
      if (typeof localStorage !== "undefined") {
        try {
          const _raw = localStorage.getItem("auth-state");
          const _token = JSON.parse(_raw ?? "{}")?.state?.providerData?.accessToken;
          if (_token) {
            const _payload = JSON.parse(atob(_token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/")));
            console.log("[getConsumers] user permission:", _payload["https://api.securitydatapoint.com/permission"]);
            console.log("[getConsumers] user tier:", _payload["https://api.securitydatapoint.com/tier"]);
          }
        } catch (e) { console.warn("[getConsumers] token decode error:", e); }
      }

      // Read auth state. The context passed to apiKeys.getConsumers is a ZudokuContext
      // but its shape differs across builds. Use the access token directly — the same
      // pattern used in UserManagementPage.tsx — as the reliable cross-build approach.
      const isSuperAdmin = (): boolean => {
        if (typeof localStorage === "undefined") return false; // SSR
        try {
          const raw = localStorage.getItem("auth-state");
          if (!raw) return false;
          const providerData = JSON.parse(raw)?.state?.providerData;
          // Auth0/OpenID stores the access token in providerData.accessToken
          const token = providerData?.accessToken;
          if (!token) return false;
          const payload = JSON.parse(atob(token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/")));
          return payload["https://api.securitydatapoint.com/permission"] === "superadmin";
        } catch {
          return false;
        }
      };

      // During SSR prerender localStorage is unavailable — the outer catch handles it
      try {
        if (!isSuperAdmin()) { console.warn("[getConsumers] isSuperAdmin=false, returning []"); return []; }

        // Get access token for Bearer auth
        const _authRaw = localStorage.getItem("auth-state");
        const accessToken = JSON.parse(_authRaw!)?.state?.providerData?.accessToken as string | undefined;
        if (!accessToken) { console.warn("[getConsumers] no accessToken"); return []; }

        // Deployment name is baked into ZUPLO_BUILD_CONFIG at build time
        const deploymentName = (_metaEnv.ZUPLO_BUILD_CONFIG
          ? JSON.parse(_metaEnv.ZUPLO_BUILD_CONFIG).deploymentName
          : undefined) as string | undefined;
        if (!deploymentName) { console.warn("[getConsumers] no deploymentName"); return []; }

        // Zudoku monkey-patches window.fetch to proxy all requests through the user's
        // gateway, so we use the native fetch captured at module init time instead.
        const _fetch = _nativeFetch ?? fetch;
        const response = await _fetch(
          `https://api.zuploedge.com/v2/client/${deploymentName}/consumers`,
          { headers: { Authorization: `Bearer ${accessToken}` } },
        );
        if (!response.ok) return [];

        const raw2 = await response.json();
        const items: any[] = Array.isArray(raw2) ? raw2 : (raw2?.data ?? []);

        return items.map((consumer: any) => {
          const keys: any[] = consumer.currentKeys ?? consumer.apiKeys?.data ?? [];
          return {
            id: consumer.id,
            label: consumer.label ?? consumer.name ?? consumer.subject ?? "API Key",
            createdOn: consumer.createdOn,
            updatedOn: consumer.updatedOn,
            expiresOn: consumer.expiresOn,
            apiKeys: keys,
            key: keys[0],
          };
        });
      } catch (e) {
        console.error("[getConsumers] error:", e);
        return [];
      }
    },
    },
};

export default config;
